import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, ArrowUpRight, Target, FileText, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, AreaChart, Area, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';
import {
  API, PLATFORM_ICONS, int, tl, tl2, pct, x2, short, div, toISO, trDate, PRESETS, presetRange, flat, trLabel,
  baseName, aggregateAds, Delta, Tabs, Loading, ErrorBox,
  AdsSummary, SiteOverview, SourceMedium, UserAttributes, SearchTerms, Pages, Products,
} from './DynamicTracking';

// Yataş Grubu — Performans Panosu. Mevcut Overview'a dokunulmadı (7 Eki 2026, Efe): grup için
// ayrı, yalnız 4 Yataş markasında görünen giriş sayfası. Markanın mailindeki "Dinamik Takip
// Alanı"nın yeni yüzü: üstte "bir bakışta" bloğu (ay hedefi + ay sonu tahmini, 12 aylık
// hedef/gerçekleşen, harcama dağılımı, öne çıkanlar, son bülten), altında Dinamik Takip'in
// detay bölümleri (aynı bileşenler, aynı kaynaklar). Ek kaynak: /api/yatas-kpi/overview.

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
// Eksen: 1,5 Mn gibi ara değerler "2Mn"a yuvarlanıp tekrar etmesin
const axisTick = (v) => {
  const a = Math.abs(v);
  const f = (x) => x.toLocaleString('tr-TR', { maximumFractionDigits: 1 });
  return a >= 1e6 ? `${f(v / 1e6)}Mn` : a >= 1e3 ? `${f(v / 1e3)}Bin` : f(v);
};
const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const DEVICE_TR = { mobile: 'Mobil', desktop: 'Masaüstü', tablet: 'Tablet', 'smart tv': 'Akıllı TV' };
// Bültenle aynı, bizim sunduğumuz logolar (markanın kendi logoUrl'i başka alan adında, her zaman yüklenmiyor)
const ASSET = 'https://api.glowytics.com/pixel';
const LOGOS = {
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7': `${ASSET}/yatas-bedding-logo-lacivert.png`,
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e': `${ASSET}/enza-assets/enza-logo.svg`,
};

function BrandLogo({ brand }) {
  const [broken, setBroken] = useState(false);
  const src = LOGOS[brand?.id] || brand?.logoUrl;
  useEffect(() => setBroken(false), [src]);
  if (!src || broken) return null;
  return (
    <div className="flex h-11 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white px-2 py-1.5">
      <img src={src} alt={brand?.name || ''} onError={() => setBroken(true)} className="max-h-full max-w-full object-contain" />
    </div>
  );
}

const Card = ({ className = '', children }) => (
  <div className={`min-w-0 rounded-3xl border border-gray-200/80 bg-white p-4 sm:p-5 ${className}`}>{children}</div>
);
const CardTitle = ({ icon: Icon, title, note, right }) => (
  <div className="mb-3 flex items-start justify-between gap-2">
    <div className="min-w-0">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">{Icon && <Icon className="h-4 w-4 text-gray-400" />}{title}</h3>
      {note && <p className="mt-0.5 text-[11px] text-gray-500">{note}</p>}
    </div>
    {right}
  </div>
);

function Spark({ data, color, id }) {
  if (!data?.length) return null;
  return (
    <div className="mt-2 h-10">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} /><stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.6} fill={`url(#${id})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function HeroKpi({ label, value, cur, prev, invert, spark, color = '#3B82F6', id, hint }) {
  return (
    <div className="min-w-0 rounded-2xl border border-gray-200/80 bg-white px-4 pb-2 pt-3" title={hint}>
      <div className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="truncate text-xl font-semibold tabular-nums text-gray-900 sm:text-2xl">{value}</span>
        {prev !== undefined && <span className="shrink-0 text-xs"><Delta cur={cur} prev={prev} invert={invert} /></span>}
      </div>
      {spark ? <Spark data={spark} color={color} id={id} /> : <div className="h-3" />}
    </div>
  );
}

// ── Ay hedefi ───────────────────────────────────────────────────────────────
// Gerçekleşen = Target vs Actual sayfasının aylık toplamları (DB), hedef = aynı sayfada girilen hedef.
function MonthTarget({ kpi, month, year, commerce }) {
  const row = kpi.data?.months?.find((m) => m.month === month);
  const a = row?.actuals || {}; const t = row?.targets || {};
  const now = new Date();
  const isCurrent = now.getFullYear() === year && now.getMonth() + 1 === month;
  const days = new Date(year, month, 0).getDate();
  // Veri dün itibarıyla tam → geçen gün = bugün - 1 (ayın 1'inde tahmin yok)
  const elapsed = isCurrent ? now.getDate() - 1 : days;
  const project = (v) => (isCurrent && elapsed > 0 && v != null ? (v / elapsed) * days : null);
  const costTarget = t.cost ?? ((t.cost_branding || 0) + (t.cost_performans || 0) || null);
  // E-ticareti olmayan markada (Divanev) ciro/işlem yerine gösterim ve izlenme hedefi
  const items = (commerce ? [
    { key: 'revenue', label: 'Ciro', actual: a.revenue, target: t.revenue, fmt: tl },
    { key: 'sessions', label: 'Oturum', actual: a.sessions, target: t.sessions, fmt: short },
    { key: 'transactions', label: 'İşlem', actual: a.transactions, target: t.transactions, fmt: int },
  ] : [
    { key: 'sessions', label: 'Oturum', actual: a.sessions, target: t.sessions, fmt: short },
    { key: 'impressions', label: 'Gösterim', actual: a.impressions, target: t.impressions, fmt: short },
    { key: 'views', label: 'İzlenme', actual: a.views, target: t.views, fmt: short },
  ]).concat([{ key: 'cost', label: 'Reklam harcaması', actual: a.cost, target: costTarget, fmt: tl, spend: true }]);
  const timePct = (elapsed / days) * 100;

  return (
    <Card className="xl:col-span-5">
      <CardTitle icon={Target} title={`${MONTHS[month - 1]} ${year} · hedefe göre`}
        note={isCurrent
          ? `${elapsed}/${days} gün geçti (%${Math.round(timePct)}). Tahmin, bugüne kadarki günlük ortalamayla ay sonu.`
          : 'Ay kapandı. Hedefler Hedef vs Gerçekleşen sayfasından.'}
        right={<Link to="/labs/kpi-targets" className="shrink-0 text-[11px] font-medium text-blue-600 hover:text-blue-800">Hedefleri gör</Link>} />
      {kpi.loading && !kpi.data ? <Loading h={200} /> : kpi.error ? <ErrorBox text={kpi.error} /> : (
        <div className="space-y-4">
          {items.map((it) => {
            const proj = project(it.actual);
            const done = it.target ? (it.actual / it.target) * 100 : null;
            const projPct = it.target && proj != null ? (proj / it.target) * 100 : null;
            // Harcamada hedefi aşmak "iyi" değil → renk nötr; diğerlerinde tahmin %100 altında kalırsa uyarı
            const tone = it.spend ? 'bg-slate-500' : (projPct ?? done ?? 0) >= 100 ? 'bg-emerald-500' : (projPct ?? done ?? 0) >= 85 ? 'bg-amber-400' : 'bg-rose-400';
            return (
              <div key={it.key}>
                <div className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="font-medium text-gray-700">{it.label}</span>
                  <span className="tabular-nums text-gray-500">
                    <b className="font-semibold text-gray-900">{it.fmt(it.actual)}</b>
                    {it.target ? <> / {it.fmt(it.target)} · <b className="font-semibold text-gray-800">%{Math.round(done)}</b></> : ' · hedef girilmemiş'}
                  </span>
                </div>
                <div className="relative mt-1.5 h-2.5 overflow-hidden rounded-full bg-gray-100">
                  {projPct != null && <div className="absolute inset-y-0 left-0 rounded-full bg-gray-200" style={{ width: `${Math.min(projPct, 100)}%` }} />}
                  <div className={`absolute inset-y-0 left-0 rounded-full ${tone}`} style={{ width: `${Math.min(done || 0, 100)}%` }} />
                  {isCurrent && <div className="absolute inset-y-0 w-px bg-gray-900/40" style={{ left: `${timePct}%` }} title="Ayın geçen kısmı" />}
                </div>
                {proj != null && it.target && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    Ay sonu tahmini <b className="font-medium text-gray-700">{it.fmt(proj)}</b> · hedefin %{Math.round(projPct)}'i
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── 12 ay: hedef vs gerçekleşen ─────────────────────────────────────────────
function YearTrend({ kpi, year, month, commerce }) {
  // commerce veri gelince belli olur; kullanıcı sekmeye dokunmadıysa varsayılanı ona göre ayarla
  const [picked, setPicked] = useState(null);
  const metric = picked && (commerce || picked !== 'revenue') ? picked : (commerce ? 'revenue' : 'sessions');
  const setMetric = setPicked;
  const tabs = commerce
    ? [['revenue', 'Ciro'], ['sessions', 'Oturum'], ['cost', 'Harcama']]
    : [['sessions', 'Oturum'], ['impressions', 'Gösterim'], ['cost', 'Harcama']];
  const data = useMemo(() => (kpi.data?.months || []).map((m) => {
    const t = m.targets || {};
    const target = metric === 'cost' ? (t.cost ?? ((t.cost_branding || 0) + (t.cost_performans || 0) || null)) : t[metric];
    const future = m.month > month;
    return { m: MONTHS_SHORT[m.month - 1], actual: future ? null : m.actuals?.[metric] || null, target: target || null, current: m.month === month };
  }), [kpi.data, metric, month]);
  const isCount = metric === 'sessions' || metric === 'impressions';
  const fmt = isCount ? int : tl;
  return (
    <Card className="xl:col-span-7">
      <CardTitle icon={TrendingUp} title={`${year} · aylık gerçekleşen ve hedef`}
        note="Çubuk gerçekleşen, çizgi hedef. Seçili ay koyu."
        right={<Tabs size="sm" value={metric} onChange={setMetric} items={tabs} />} />
      {kpi.loading && !kpi.data ? <Loading h={240} /> : kpi.error ? <ErrorBox text={kpi.error} /> : (
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} width={52}
                tickFormatter={(v) => (isCount ? axisTick(v) : `₺${axisTick(v)}`)} />
              <Tooltip formatter={(v, k) => [fmt(v), k === 'actual' ? 'Gerçekleşen' : 'Hedef']} labelStyle={{ color: '#334155' }} />
              <Bar dataKey="actual" radius={[6, 6, 0, 0]} maxBarSize={34}>
                {data.map((d) => <Cell key={d.m} fill={d.current ? '#1E3A8A' : '#93C5FD'} />)}
              </Bar>
              <Line type="monotone" dataKey="target" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2.5, fill: '#F59E0B' }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ── Harcama dağılımı ────────────────────────────────────────────────────────
const BUCKET_COLORS = { branding: '#6366F1', performance: '#10B981', other: '#CBD5E1' };
function SpendMix({ ads }) {
  const d = ads.data;
  const mix = useMemo(() => {
    if (!d) return null;
    const all = aggregateAds(d.current, 'all').cost;
    const b = aggregateAds(d.current, 'branding').cost;
    const p = aggregateAds(d.current, 'performance').cost;
    const parts = [
      { key: 'branding', label: 'Branding', v: b },
      { key: 'performance', label: 'Performans', v: p },
      { key: 'other', label: 'Adında ayrım yok', v: Math.max(0, all - b - p) },
    ].filter((x) => x.v >= 1);
    const byPl = new Map();
    for (const r of d.current || []) byPl.set(r.platform, (byPl.get(r.platform) || 0) + r.cost);
    const platforms = d.platforms.filter((pl) => byPl.get(pl.key) > 0)
      .map((pl) => ({ ...pl, cost: byPl.get(pl.key) })).sort((x, y) => y.cost - x.cost);
    return { all, parts, platforms };
  }, [d]);
  return (
    <Card className="xl:col-span-4">
      <CardTitle title="Harcama dağılımı" note="Seçili dönem, reklam platformları." />
      {ads.loading && !d ? <Loading h={200} /> : ads.error ? <ErrorBox text={ads.error} /> : mix && (
        <>
          <div className="flex items-center gap-4">
            <div className="relative h-[120px] w-[120px] shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={mix.parts} dataKey="v" nameKey="label" innerRadius={40} outerRadius={58} paddingAngle={2} stroke="none" isAnimationActive={false}>
                    {mix.parts.map((x) => <Cell key={x.key} fill={BUCKET_COLORS[x.key]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[10px] text-gray-400">Toplam</span>
                <span className="text-xs font-semibold tabular-nums text-gray-900">₺{short(mix.all)}</span>
              </div>
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              {mix.parts.map((x) => (
                <div key={x.key} className="text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-gray-700"><i className="inline-block h-2 w-2 rounded-full" style={{ background: BUCKET_COLORS[x.key] }} />{x.label}</span>
                    <span className="tabular-nums text-gray-500">{pct((x.v / mix.all) * 100)}</span>
                  </div>
                  <div className="pl-3.5 tabular-nums text-gray-900">{tl(x.v)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 space-y-2 border-t border-gray-100 pt-3">
            {mix.platforms.map((pl) => (
              <div key={pl.key} className="flex items-center gap-2 text-xs">
                <img src={PLATFORM_ICONS[pl.key]} alt="" className="h-4 w-4 shrink-0 object-contain" />
                <span className="w-20 shrink-0 truncate text-gray-700">{pl.label}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-slate-700" style={{ width: `${(pl.cost / mix.all) * 100}%` }} />
                </div>
                <span className="w-20 shrink-0 whitespace-nowrap text-right tabular-nums text-gray-600">₺{short(pl.cost)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

// ── Öne çıkanlar (kaynak / ürün / şehir+cihaz) ──────────────────────────────
function TopList({ title, note, rows, value, fmt, sub, href }) {
  const max = Math.max(...rows.map(value), 0);
  return (
    <Card className="xl:col-span-4">
      <CardTitle title={title} note={note}
        right={href && <a href={href} onClick={(e) => { e.preventDefault(); document.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth' }); }}
          className="shrink-0 text-[11px] font-medium text-blue-600 hover:text-blue-800">Tümü</a>} />
      {rows.length === 0 ? <div className="py-6 text-center text-sm text-gray-400">Veri yok.</div> : (
        <ol className="space-y-2.5">
          {rows.map((r, i) => (
            <li key={r.label} className="text-xs">
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="w-3 shrink-0 text-right tabular-nums text-gray-400">{i + 1}</span>
                  <span className="truncate text-gray-800" title={r.label}>{r.label}</span>
                </span>
                <span className="shrink-0 tabular-nums font-medium text-gray-900">{fmt(value(r))}</span>
              </div>
              <div className="ml-5 mt-1 flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-blue-500/70" style={{ width: `${max ? (value(r) / max) * 100 : 0}%` }} />
                </div>
                {sub && <span className="shrink-0 text-[10px] tabular-nums text-gray-400">{sub(r)}</span>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function Highlights({ ga, commerce }) {
  const d = ga.data;
  const srcKey = commerce ? 'totalRevenue' : 'sessions';
  const sources = useMemo(() => flat(d?.sourceMedium).sort((a, b) => (b[srcKey] || 0) - (a[srcKey] || 0)).slice(0, 6), [d, srcKey]);
  const landing = useMemo(() => flat(d?.landingPage).filter((r) => r.label && r.label !== '(not set)')
    .sort((a, b) => (b.sessions || 0) - (a.sessions || 0)).slice(0, 6), [d]);
  const products = useMemo(() => {
    const m = new Map();
    for (const r of flat(d?.items)) {
      if (!r.label || r.label === '(not set)') continue;
      const k = baseName(r.label);
      const x = m.get(k) || { label: k, itemRevenue: 0, itemsPurchased: 0 };
      x.itemRevenue += r.itemRevenue || 0; x.itemsPurchased += r.itemsPurchased || 0;
      m.set(k, x);
    }
    return [...m.values()].sort((a, b) => b.itemRevenue - a.itemRevenue).slice(0, 6);
  }, [d]);
  const cities = useMemo(() => flat(d?.city, (x) => trLabel({})(x[0])).filter((r) => r.label !== 'Belirtilmemiş')
    .sort((a, b) => (b.sessions || 0) - (a.sessions || 0)).slice(0, 6), [d]);
  const total = d?.totals?.[0]?.cur || {};
  if (ga.loading && !d) return <Card className="xl:col-span-12"><Loading text="GA4'ten çekiliyor…" /></Card>;
  if (ga.error) return <Card className="xl:col-span-12"><ErrorBox text={ga.error} /></Card>;
  return (
    <>
      {commerce ? (
        <TopList title="Ciro getiren kaynaklar" note="Kaynak / ortam, GA4 cirosu" href="#kaynak" rows={sources}
          value={(r) => r.totalRevenue || 0} fmt={(v) => `₺${short(v)}`} sub={(r) => `${short(r.sessions)} oturum`} />
      ) : (
        <TopList title="Trafik getiren kaynaklar" note="Kaynak / ortam, oturum" href="#kaynak" rows={sources}
          value={(r) => r.sessions || 0} fmt={short} sub={(r) => pct(total.sessions ? (r.sessions / total.sessions) * 100 : 0)} />
      )}
      {commerce ? (
        <TopList title="En çok satan ürünler" note="Varyantlar birleşik, ürün cirosu" href="#urun" rows={products}
          value={(r) => r.itemRevenue} fmt={(v) => `₺${short(v)}`} sub={(r) => `${int(r.itemsPurchased)} adet`} />
      ) : (
        <TopList title="Açılış sayfaları" note="Oturumun başladığı sayfa" href="#sayfa" rows={landing}
          value={(r) => r.sessions || 0} fmt={short} sub={(r) => `hemen çıkma ${pct((r.bounceRate || 0) * 100)}`} />
      )}
      <TopList title="Şehirler" note="Oturuma göre" href="#kullanici" rows={cities}
        value={(r) => r.sessions || 0} fmt={short} sub={(r) => pct(total.sessions ? (r.sessions / total.sessions) * 100 : 0)} />
    </>
  );
}

// ── Son bülten ──────────────────────────────────────────────────────────────
function BulletinCard({ kpi, commerce }) {
  const now = new Date();
  const bm = now.getMonth() === 0 ? 12 : now.getMonth(); // kapanmış son ay
  const by = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const row = by === kpi.data?.year ? kpi.data?.months?.find((m) => m.month === bm) : null;
  const a = row?.actuals || {}; const t = row?.targets || {};
  const vsTarget = (k) => (t[k] && a[k] != null ? `hedefin %${Math.round((a[k] / t[k]) * 100)}'i` : null);
  const stats = commerce ? [
    ['Ciro', tl(a.revenue), vsTarget('revenue')],
    ['Oturum', short(a.sessions), vsTarget('sessions')],
    ['Harcama', tl(a.cost), null],
    ['ROAS', x2(a.roas), null],
  ] : [
    ['Oturum', short(a.sessions), vsTarget('sessions')],
    ['Gösterim', short(a.impressions), vsTarget('impressions')],
    ['Harcama', tl(a.cost), null],
    ['İzlenme', short(a.views), vsTarget('views')],
  ];
  return (
    <div className="flex min-w-0 flex-col justify-between rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-950 p-4 text-white sm:p-5 xl:col-span-3">
      <div>
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-indigo-200"><FileText className="h-3.5 w-3.5" /> Aylık bülten</div>
        <div className="mt-1 text-xl font-semibold">{MONTHS[bm - 1]} {by}</div>
        <p className="mt-1 text-xs text-slate-300">Ayın özeti, kampanya ve kreatifler, gelecek ay planı.</p>
        <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-3">
          {stats.map(([k, v, s]) => (
            <div key={k} className="min-w-0">
              <dt className="text-[10px] uppercase tracking-wide text-slate-400">{k}</dt>
              <dd className="truncate text-base font-semibold tabular-nums">{kpi.data ? v : '…'}</dd>
              {s && <dd className="text-[10px] text-indigo-200">{s}</dd>}
            </div>
          ))}
        </dl>
      </div>
      <Link to="/yatas/bulten" className="mt-5 inline-flex items-center justify-center gap-1 rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-indigo-50">
        Bülteni aç <ArrowUpRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

// Yataş Bedding alt markaları: reklam/arama terimi kampanya adından, GA4 kampanya adı ya da açılış
// sayfasından, ürünler ürün kodundan ayrılır (backend: yatasTracking resolveSub).
const YATAS_BEDDING_ID = '44e35dfe-0186-495d-a0cc-db5e7e459ab7';
const SUB_BRANDS = [['urban', 'Urban Mood Sleep'], ['dormy', 'Dormy Home']];

// ── Sayfa ────────────────────────────────────────────────────────────────────
export default function PerformancePanel() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const [preset, setPreset] = useState('thisMonth');
  const [range, setRange] = useState(() => presetRange('thisMonth'));
  const [ads, setAds] = useState({ loading: true });
  const [ga, setGa] = useState({ loading: true });
  const [st, setSt] = useState({ loading: true });
  const [kpi, setKpi] = useState({ loading: true });
  const [refreshKey, setRefreshKey] = useState(0);
  const [sub, setSub] = useState('');
  const reqRef = useRef(0);
  const hasSubs = brandId === YATAS_BEDDING_ID;
  useEffect(() => { setSub(''); }, [brandId]);
  const subLabel = SUB_BRANDS.find(([k]) => k === sub)?.[1];

  // Ay hedefi bloğu seçili dönemin BİTTİĞİ ayı gösterir
  const endYear = Number(range[1].slice(0, 4));
  const endMonth = Number(range[1].slice(5, 7));

  const getJson = useCallback(async (url) => {
    const r = await fetch(url, { credentials: 'include', headers: { 'X-Brand-ID': brandId } });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j;
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return;
    reqRef.current += 1;
    const id = reqRef.current;
    const qs = `start=${range[0]}&end=${range[1]}${sub ? `&sub=${sub}` : ''}${refreshKey > 0 ? '&refresh=1' : ''}`;
    const run = (kind, setter) => {
      setter((s) => ({ ...s, loading: true, error: null }));
      getJson(`${API}/api/yatas-tracking/${kind}?${qs}`)
        .then((j) => { if (id === reqRef.current) setter({ loading: false, data: j }); })
        .catch((e) => { if (id === reqRef.current) setter({ loading: false, error: e.message }); });
    };
    run('ads', setAds); run('ga', setGa); run('search-terms', setSt);
  }, [brandId, range, sub, refreshKey, getJson]);

  useEffect(() => {
    if (!brandId) return;
    setKpi((s) => ({ ...s, loading: true, error: null }));
    getJson(`${API}/api/yatas-kpi/overview?year=${endYear}`)
      .then((j) => setKpi({ loading: false, data: j }))
      .catch((e) => setKpi({ loading: false, error: e.message }));
  }, [brandId, endYear, refreshKey, getJson]);

  const pickPreset = (k) => { setPreset(k); setRange(presetRange(k)); };
  const setCustom = (i, v) => {
    if (!v) return;
    setPreset('custom');
    setRange((r) => { const n = [...r]; n[i] = v; if (n[0] > n[1]) n[1 - i] = v; return n; });
  };

  // Üst KPI'lar: site geneli (GA4) + reklam harcaması
  const gc = ga.data?.totals?.[0]?.cur || {}; const gp = ga.data?.totals?.[0]?.prev || {};
  const adsCur = useMemo(() => aggregateAds(ads.data?.current, 'all'), [ads.data]);
  const adsPrev = useMemo(() => aggregateAds(ads.data?.previous, 'all'), [ads.data]);
  const spark = useMemo(() => {
    const rows = ga.data?.daily || [];
    return {
      sessions: rows.map((r) => ({ v: r.cur?.sessions || 0 })),
      revenue: rows.map((r) => ({ v: r.cur?.totalRevenue || 0 })),
      tx: rows.map((r) => ({ v: r.cur?.transactions || 0 })),
      cr: rows.map((r) => ({ v: r.cur?.sessions ? (r.cur.transactions / r.cur.sessions) * 100 : 0 })),
    };
  }, [ga.data]);
  const device = useMemo(() => {
    const rows = flat(ga.data?.device, (x) => trLabel(DEVICE_TR)(x[0]));
    const tot = rows.reduce((a, r) => a + (r.sessions || 0), 0);
    const mobile = rows.find((r) => r.dims[0] === 'mobile');
    return tot && mobile ? (mobile.sessions / tot) * 100 : null;
  }, [ga.data]);

  // E-ticaret var mı? Yılın herhangi bir ayında ciro ya da dönemde GA4 cirosu → ciro kartları;
  // yoksa (Divanev) trafik/maliyet kartları.
  const commerce = useMemo(() => (kpi.data?.months || []).some((m) => (m.actuals?.revenue || 0) > 0)
    || (ga.data?.totals?.[0]?.cur?.totalRevenue || 0) > 0, [kpi.data, ga.data]);

  const rg = ga.data?.range || ads.data?.range;
  const loadingAny = ga.loading || ads.loading || st.loading || kpi.loading;
  const NAV = [
    ['ozet', 'Bir bakışta'], ['reklam', 'Reklam Özeti'], ['genel', 'Site Geneli'], ['kaynak', 'Kaynak / Ortam'],
    ['kullanici', 'Kullanıcı Özellikleri'], ['arama', 'Arama Terimleri'], ['sayfa', 'Açılış Sayfası & Sayfa Yolu'], ['urun', 'E-ticaret Ürün'],
  ];

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 px-3 pb-16 pt-2 sm:px-5">
      {/* Başlık + tarih */}
      <div className="sticky top-0 z-20 -mx-3 border-b border-gray-200/70 bg-white/90 px-3 py-3 backdrop-blur sm:-mx-5 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <BrandLogo brand={selectedBrand} />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900">Performans Panosu</h1>
              <p className="truncate text-xs text-gray-500">
                {selectedBrand?.name}{subLabel && <> › <b className="font-semibold text-gray-700">{subLabel}</b></>} · {trDate(range[0])} – {trDate(range[1])}
                {rg && <> · ▲▼ önceki dönem: {trDate(rg.prevStart)} – {trDate(rg.prevEnd)}</>}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={preset} onChange={pickPreset} items={PRESETS} size="sm" />
            <div className="flex items-center gap-1 text-sm">
              <input type="date" value={range[0]} max={range[1]} onChange={(e) => setCustom(0, e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700" />
              <span className="text-gray-400">–</span>
              <input type="date" value={range[1]} min={range[0]} max={toISO(new Date())} onChange={(e) => setCustom(1, e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700" />
            </div>
            <button onClick={() => setRefreshKey((k) => k + 1)} title="Verileri yeniden çek"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
              <RefreshCw className={`h-3.5 w-3.5 ${loadingAny ? 'animate-spin' : ''}`} /> Yenile
            </button>
            {hasSubs && (
              <div className="flex w-full items-center justify-end gap-2">
                <span className="text-xs text-gray-500">Marka</span>
                <select value={sub} onChange={(e) => setSub(e.target.value)}
                  title="Alt markalar kampanya adından ve gittiği sayfalardan ayrılır"
                  className={`rounded-lg border px-2 py-1 text-xs ${sub ? 'border-blue-300 bg-blue-50 font-medium text-blue-800' : 'border-gray-200 text-gray-700'}`}>
                  <option value="">{selectedBrand?.name || 'Yataş Bedding'}</option>
                  <optgroup label="Alt Markalar">
                    {SUB_BRANDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </optgroup>
                </select>
              </div>
            )}
          </div>
        </div>
        {subLabel && (
          <p className="mt-1.5 text-[11px] text-gray-500">
            <b className="font-medium text-gray-700">{subLabel}</b>: reklam ve arama terimleri adında “{subLabel}” geçen kampanyalardan; site verisi bu kampanyalardan gelen ya da {subLabel} sayfalarından (marka sayfası + ürün sayfaları) açılan oturumlardan; ürün tablosu {subLabel} ürünlerinden. Ay hedefi, yıllık hedef ve bülten marka geneli olduğu için gizlendi.
          </p>
        )}
        <nav className="mt-2 flex gap-1 overflow-x-auto text-xs">
          {NAV.map(([id, label]) => (
            <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
              className="whitespace-nowrap rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
              {label}
            </a>
          ))}
        </nav>
      </div>

      {/* Bir bakışta */}
      <section id="ozet" className="scroll-mt-28 space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {commerce ? (
            <>
              <HeroKpi id="spRev" label="Ciro" value={gc.totalRevenue != null ? `₺${short(gc.totalRevenue)}` : '—'} cur={gc.totalRevenue} prev={gp.totalRevenue} spark={spark.revenue} color="#10B981" hint={`GA4, tüm kanallar · ${tl(gc.totalRevenue)}`} />
              <HeroKpi id="spSes" label="Oturum" value={short(gc.sessions)} cur={gc.sessions} prev={gp.sessions} spark={spark.sessions} hint="GA4, tüm kanallar" />
              <HeroKpi id="spTx" label="İşlem" value={int(gc.transactions)} cur={gc.transactions} prev={gp.transactions} spark={spark.tx} color="#8B5CF6" />
              <HeroKpi id="spCr" label="Dönüşüm oranı" value={gc.sessions ? `%${(gc.transactions / gc.sessions * 100).toFixed(2).replace('.', ',')}` : '—'}
            cur={div(gc.transactions, gc.sessions)} prev={div(gp.transactions, gp.sessions)} spark={spark.cr} color="#F59E0B" />
              <HeroKpi label="Reklam harcaması" value={ads.data ? `₺${short(adsCur.cost)}` : '—'} cur={adsCur.cost} prev={adsPrev.cost} invert
            hint="Reklam platformları toplamı (medya satın alma hariç)" />
              <HeroKpi label="ROAS (ciro / harcama)" value={x2(div(gc.totalRevenue, adsCur.cost))}
            cur={div(gc.totalRevenue, adsCur.cost)} prev={div(gp.totalRevenue, adsPrev.cost)}
            hint="Site cirosu (GA4, tüm kanallar) / reklam harcaması" />
            </>
          ) : (
            <>
              <HeroKpi id="spSes" label="Oturum" value={short(gc.sessions)} cur={gc.sessions} prev={gp.sessions} spark={spark.sessions} hint="GA4, tüm kanallar" />
              <HeroKpi label="Kullanıcı" value={short(gc.totalUsers)} cur={gc.totalUsers} prev={gp.totalUsers} />
              <HeroKpi label="Yeni kullanıcı" value={short(gc.newUsers)} cur={gc.newUsers} prev={gp.newUsers} />
              <HeroKpi label="Sitede kalma süresi" value={gc.averageSessionDuration ? `${Math.floor(gc.averageSessionDuration / 60)}:${String(Math.round(gc.averageSessionDuration % 60)).padStart(2, '0')}` : '—'}
                cur={gc.averageSessionDuration} prev={gp.averageSessionDuration} />
              <HeroKpi label="Reklam harcaması" value={ads.data ? `₺${short(adsCur.cost)}` : '—'} cur={adsCur.cost} prev={adsPrev.cost} invert
                hint="Reklam platformları toplamı (medya satın alma hariç)" />
              <HeroKpi label="Oturum başı maliyet" value={tl2(div(adsCur.cost, gc.sessions))}
                cur={div(adsCur.cost, gc.sessions)} prev={div(adsPrev.cost, gp.sessions)} invert
                hint="Reklam harcaması / sitenin tüm oturumları" />
            </>
          )}
        </div>

        {!sub && <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <MonthTarget kpi={kpi} month={endMonth} year={endYear} commerce={commerce} />
          <YearTrend kpi={kpi} year={endYear} month={endMonth} commerce={commerce} />
        </div>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-12">
          <SpendMix ads={ads} />
          <Card className={sub ? 'xl:col-span-8' : 'xl:col-span-5'}>
            <CardTitle title="Ziyaretçi profili" note="GA4, seçili dönem." />
            {ga.loading && !ga.data ? <Loading h={200} /> : ga.error ? <ErrorBox text={ga.error} /> : (
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Yeni kullanıcı', short(gc.newUsers), gc.totalUsers ? `kullanıcıların ${pct((gc.newUsers / gc.totalUsers) * 100)}` : null, gc.newUsers, gp.newUsers],
                  ['Mobil oturum payı', pct(device), 'cihaz kırılımından', null, null],
                  ['Sitede kalma süresi', gc.averageSessionDuration ? `${Math.floor(gc.averageSessionDuration / 60)}:${String(Math.round(gc.averageSessionDuration % 60)).padStart(2, '0')}` : '—', 'oturum ortalaması', gc.averageSessionDuration, gp.averageSessionDuration],
                  ['Hemen çıkma', pct((gc.bounceRate || 0) * 100), null, gc.bounceRate, gp.bounceRate, true],
                  commerce
                    ? ['Sepet ortalaması', tl(div(gc.totalRevenue, gc.transactions)), null, div(gc.totalRevenue, gc.transactions), div(gp.totalRevenue, gp.transactions)]
                    : ['Etkileşimli oturum', pct((gc.engagementRate || 0) * 100), 'GA4 etkileşim oranı', gc.engagementRate, gp.engagementRate],
                  ['Sayfa / oturum', gc.sessions ? (gc.screenPageViews / gc.sessions).toFixed(2).replace('.', ',') : '—', null, div(gc.screenPageViews, gc.sessions), div(gp.screenPageViews, gp.sessions)],
                ].map(([k, v, s, c, p, inv]) => (
                  <div key={k} className="rounded-2xl bg-gray-50 px-3 py-2.5">
                    <div className="text-[11px] text-gray-500">{k}</div>
                    <div className="mt-0.5 flex items-baseline justify-between gap-2">
                      <span className="text-base font-semibold tabular-nums text-gray-900">{v}</span>
                      {c != null && p != null && <span className="text-[11px]"><Delta cur={c} prev={p} invert={inv} /></span>}
                    </div>
                    {s && <div className="text-[10px] text-gray-400">{s}</div>}
                  </div>
                ))}
              </div>
            )}
          </Card>
          {!sub && <BulletinCard kpi={kpi} commerce={commerce} />}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-12">
          <Highlights ga={ga} commerce={commerce} />
        </div>
      </section>

      {/* Detaylar — Dinamik Takip ile aynı bölümler */}
      <div className="flex items-center gap-3 pt-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Detaylı raporlar</h2>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
      <AdsSummary ads={ads} ga={ga} />
      <SiteOverview ga={ga} />
      <SourceMedium ga={ga} />
      <UserAttributes ga={ga} />
      <SearchTerms st={st} />
      <Pages ga={ga} />
      <Products ga={ga} />
    </div>
  );
}
